import React from 'react';
import type { WorkoutSet, Exercise } from '../types';

interface SetDisplayProps {
  set: WorkoutSet;
  exercise: Exercise;
  onRemove?: () => void;
  showRemoveButton?: boolean;
}

/**
 * SetDisplay - Shows set data in appropriate format based on exercise type
 */
export const SetDisplay: React.FC<SetDisplayProps> = ({ 
  set, 
  exercise, 
  onRemove, 
  showRemoveButton = true 
}) => {
  const exerciseType = exercise.exercise_type || 'вес_повторения';

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}с`;
  };

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)}км`;
    }
    return `${meters}м`;
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const renderSetData = () => {
    switch (exerciseType) {
      case 'вес_повторения':
        return (
          <span>
            {set.weight !== undefined && set.weight > 0 ? `${set.weight} кг` : 'собств. вес'} × {set.reps}
          </span>
        );

      case 'собственный_вес_повторения':
        return (
          <span>
            {set.reps} повторов
            {set.bodyweight_modifier && (
              <span className="text-sm text-blue-600 ml-1">({set.bodyweight_modifier})</span>
            )}
          </span>
        );

      case 'на_время':
        return (
          <span>
            {set.duration_seconds ? formatDuration(set.duration_seconds) : 'Время не указано'}
          </span>
        );

      case 'расстояние_время':
        return (
          <span>
            {set.distance_meters ? formatDistance(set.distance_meters) : '?м'} за{' '}
            {set.duration_seconds ? formatDuration(set.duration_seconds) : '?'}
          </span>
        );

      case 'только_повторения':
        return (
          <span>
            {set.reps} раз
          </span>
        );

      default:
        return (
          <span>
            {set.weight} кг × {set.reps}
          </span>
        );
    }
  };

  return (
    <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
      <div className="flex items-center space-x-4 flex-1">
        <span className="font-medium text-gray-600">#{set.set_number}</span>
        <div className="flex-1">
          {renderSetData()}
          {set.assistance && (
            <span className="text-sm text-blue-600 ml-2">({set.assistance})</span>
          )}
          {set.notes && (
            <span className="text-sm text-gray-500 ml-2">• {set.notes}</span>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {set.completed_at && (
          <span className="text-xs text-gray-400">
            {formatTime(set.completed_at)}
          </span>
        )}
        {showRemoveButton && onRemove && (
          <button
            onClick={onRemove}
            className="text-red-600 hover:text-red-800 p-1"
            title="Удалить подход"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default SetDisplay;